import { useMemo, useState } from 'react';
import { Box, Collapse, Stack, Typography, useTheme } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { outcomeLabel, outcomeMeaning, type AnomalyOverviewResponse } from '@oip/contracts';
import { hueColor, type CardHue } from '../../components/common/hues';

/**
 * What the detector looked at, and what it declined to judge.
 *
 * Built from a customer's mockup. The tiles, the proportion bar and the
 * promotion of the refusal reasons are all its ideas and all improvements on
 * the paragraph of prose they replace — a reader could not previously answer
 * "how much of this did it actually judge" without doing the arithmetic.
 *
 * ⚠️ **FOUR departures from the mockup, each because it asks for something this
 * platform cannot say honestly.** They are recorded here rather than in a
 * commit message because every one of them is the sort of thing somebody
 * restores in good faith.
 *
 * 1. **The COLOURS.** The mockup paints *Judged* green, *Not judged* RED and
 *    *Normal* green. Green, amber and red are the four RISK LEVELS in this
 *    product — on every badge, every score and every ring — so a red tile here
 *    would claim a severity by being a colour, and the claim would be false:
 *    `NO_BASELINE` is a cold start, the expected state of every new tenant for
 *    its first three weeks, and `PLATFORM_HEALTH` is a gap on OUR side that the
 *    detector reports precisely so it is not blamed on the vehicle. A detector
 *    working exactly as designed would paint this card red on day one. Green is
 *    the same mistake inverted: it would make *Normal* read as good news and
 *    leave *Publishable* — the tile that matters — looking like the neutral one.
 *    `hues.ts` is the palette that means nothing, and this is what it is for.
 * 2. **NO second window control.** The mockup puts a date range in the card
 *    header; the page already has one, and two controls over one value is the
 *    Enabled/Availability duplication this product has already paid for — two
 *    views of one field that can visibly disagree. The card STATES the window
 *    the server resolved, which is the half that cannot drift.
 * 3. **Every refusal that HAPPENED is shown; only the ZEROES are folded away.**
 *    The mockup promotes one reason and puts the rest behind a disclosure, which
 *    is right when one dominates and misleading when two are close — a second
 *    substantial reason would be hidden with nothing saying so. Non-zero is the
 *    predicate instead, so no dominance rule has to be judged.
 * 4. **The bar's numbers live in the LEGEND.** The mockup prints the count
 *    inside each segment; a segment is as wide as its share, and 496 of 10,478
 *    is 4.7% — the number would clip or overflow its own band. Printed inside
 *    only where it fits, and in the legend always.
 */
export function DetectorSummary({ data }: { data: AnomalyOverviewResponse }): JSX.Element {
  const [showZeroes, setShowZeroes] = useState(false);

  const judged = useMemo(
    () => data.outcomes.filter((o) => !o.suppressed).reduce((n, o) => n + o.count, 0),
    [data],
  );
  const publishable = useMemo(
    () => data.outcomes.find((o) => o.outcome === 'PUBLISHABLE')?.count ?? 0,
    [data],
  );
  const refused = useMemo(() => data.outcomes.filter((o) => o.suppressed), [data]);
  const notJudged = useMemo(() => refused.reduce((n, o) => n + o.count, 0), [refused]);
  const happened = refused.filter((o) => o.count > 0);
  const never = refused.filter((o) => o.count === 0);

  const total = data.evaluated;
  /*
   * ⚠️ **Every percentage is guarded on the denominator.** A window the detector
   * has not run over has `evaluated: 0`, and `0 / 0` renders `NaN%` — which is
   * not a smaller number, it is a rendering fault on the card that exists to say
   * how much was looked at.
   */
  const pct = (n: number, of: number): string | null =>
    of > 0 ? `${Math.round((n / of) * 100)}%` : null;

  /*
   * ⚠️ **EVERY share names its denominator, because one of them is NESTED.**
   * Judged and Not judged PARTITION the candidates and sum to 100; Publishable
   * is a part of Judged, not a fourth peer — so four bare percentages down one
   * row read as a breakdown and add to 107. Reported from the built page, and
   * the mockup this was built from carried the same three figures, so it is
   * inherited rather than invented. Naming the base is what makes the nesting
   * visible without moving the tile that matters to the end of the row.
   *
   * Publishable is therefore a share of what was JUDGED, which is also the more
   * useful rate: "of everything we could compare, this much was unusual". The
   * BAR beneath is the honest three-way partition of the total and keeps its own
   * denominator — they are different questions, not two answers to one.
   */
  const ofAll = (n: number): string | null => {
    const p = pct(n, total);
    return p === null ? null : `${p} of all`;
  };
  const publishableShare = (() => {
    const p = pct(publishable, judged);
    return p === null ? null : `${p} of judged`;
  })();

  return (
    <Stack spacing={2.5}>
      <Typography variant="body2" color="text.secondary">
        {total.toLocaleString()} candidate{total === 1 ? '' : 's'} across{' '}
        {data.vehiclesConsidered.toLocaleString()} vehicle
        {data.vehiclesConsidered === 1 ? '' : 's'} · {data.from} to {data.to}
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gap: 1.5,
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
        }}
      >
        <Tile
          hue="steel"
          label="Candidates"
          value={total}
          note={`across ${data.vehiclesConsidered.toLocaleString()} vehicle${
            data.vehiclesConsidered === 1 ? '' : 's'
          }`}
        />
        <Tile
          hue="cyan"
          label="Judged"
          value={judged}
          share={ofAll(judged)}
          note={`${(judged - publishable).toLocaleString()} normal, ${publishable.toLocaleString()} publishable`}
        />
        <Tile
          hue="indigo"
          label="Publishable"
          value={publishable}
          share={publishableShare}
          /*
           * ⚠️ The vocabulary's OWN sentence, never a second copy. This tile
           * read "Unusual, on evidence worth acting on" while
           * `OUTCOME_MEANINGS` said something else — one concept, two wordings,
           * and the one on screen was the one nobody would think to update.
           * Found by screenshotting after rewriting the vocabulary.
           */
          note={outcomeMeaning('PUBLISHABLE')}
        />
        {/*
          ⚠️ "Not judged", never "failed" or "suppressed". Most of these resolve
          themselves and one of them is ours — see the departures above.
        */}
        {/*
          ⚠️ SLATE, not a colour — and it is the one hue here chosen for meaning
          rather than for variety. Grey reads as "no verdict", which is exactly
          what these are; and indigo against violet, which the first version
          drew, put the two END bands of the bar in one colour family, so the
          band that matters and the band that does not looked alike. Found by
          screenshotting, which no assertion about a hex value would have.
        */}
        <Tile
          hue="slate"
          label="Not judged"
          value={notJudged}
          share={ofAll(notJudged)}
          note="Could not be compared, for the reasons below."
        />
      </Box>

      {total > 0 && (
        <Box>
          <Typography variant="overline" color="text.secondary">
            Detector outcome
          </Typography>
          <ProportionBar
            segments={[
              { key: 'publishable', hue: 'indigo', label: 'Publishable', count: publishable },
              { key: 'normal', hue: 'cyan', label: 'Normal', count: judged - publishable },
              { key: 'notJudged', hue: 'slate', label: 'Not judged', count: notJudged },
            ]}
            total={total}
          />
        </Box>
      )}

      <Box>
        <Typography variant="overline" color="text.secondary" display="block">
          Why candidates could not be judged
        </Typography>
        {/*
          ⚠️ Each reason carries its OWN sentence, from the server. `NO_BASELINE`
          resolves itself as history accrues and `GATED_RELIABILITY` does not — a
          reader told only "suppressed" would wait for something waiting never
          fixes. The wording lives with the vocabulary, never in a second copy
          here.
        */}
        {notJudged === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Every candidate in this window was compared against a baseline.
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
            {notJudged.toLocaleString()} of {total.toLocaleString()}
            {pct(notJudged, total) ? ` (${pct(notJudged, total)})` : ''} could not be compared. The
            reasons
            need different things done about them.
          </Typography>
        )}

        <Stack spacing={1}>
          {happened.map((o) => (
            <ReasonCard
              key={o.outcome}
              outcome={o.outcome}
              count={o.count}
              meaning={o.meaning}
              share={pct(o.count, total)}
            />
          ))}
        </Stack>

        {/*
          ⚠️ **A reason that never occurred stays REACHABLE.** Absent and zero
          draw identically and mean opposite things — "the detector never hit
          that case" against "the row is missing" — so the zeroes are folded,
          never dropped. Collapsed by default because four rows all reading 0 is
          half a card carrying no information, which is how the mockup drew it.
        */}
        {never.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Stack
              component="button"
              type="button"
              direction="row"
              spacing={0.5}
              alignItems="center"
              onClick={() => setShowZeroes((v) => !v)}
              aria-expanded={showZeroes}
              sx={{
                background: 'none',
                border: 0,
                p: 0,
                cursor: 'pointer',
                color: 'text.secondary',
                font: 'inherit',
              }}
            >
              {showZeroes ? (
                <ExpandMoreIcon fontSize="small" />
              ) : (
                <ChevronRightIcon fontSize="small" />
              )}
              <Typography variant="body2" color="text.secondary">
                {never.length} reason{never.length === 1 ? '' : 's'} the detector did not hit
              </Typography>
            </Stack>
            {/*
              ⚠️ `unmountOnExit`, because MUI keeps a collapsed child MOUNTED —
              so four rows reading 0 stay in the accessibility tree and in every
              find-by-text while invisible. A screen reader would announce
              reasons the screen is not showing, and a test asserting they are
              hidden passes against markup that is not.
            */}
            <Collapse in={showZeroes} unmountOnExit>
              <Stack spacing={1} sx={{ mt: 1 }}>
                {never.map((o) => (
                  <ReasonCard
                    key={o.outcome}
                    outcome={o.outcome}
                    count={0}
                    meaning={o.meaning}
                    share={null}
                  />
                ))}
              </Stack>
            </Collapse>
          </Box>
        )}
      </Box>
    </Stack>
  );
}

/** One headline figure. */
function Tile({
  hue,
  label,
  value,
  share,
  note,
}: {
  hue: CardHue;
  label: string;
  value: number;
  share?: string | null;
  note: string;
}): JSX.Element {
  const dark = useTheme().palette.mode === 'dark';
  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
      <Typography variant="overline" sx={{ color: hueColor(hue, dark), fontWeight: 600 }}>
        {label}
      </Typography>
      <Stack direction="row" spacing={1} alignItems="baseline">
        <Typography variant="h5" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {value.toLocaleString()}
        </Typography>
        {share && (
          <Typography variant="body2" color="text.secondary">
            {share}
          </Typography>
        )}
      </Stack>
      <Typography variant="caption" color="text.secondary" display="block">
        {note}
      </Typography>
    </Box>
  );
}

/**
 * The mix as one bar.
 *
 * ⚠️ **A zero segment is not drawn at all**, rather than drawn at a minimum
 * width — a sliver for a count of nothing is a lie the legend then contradicts.
 * And the count is printed INSIDE a segment only where it fits: a band is as
 * wide as its share, and the publishable share on a real tenant is a few per
 * cent.
 */
function ProportionBar({
  segments,
  total,
}: {
  segments: ReadonlyArray<{ key: string; hue: CardHue; label: string; count: number }>;
  total: number;
}): JSX.Element {
  const dark = useTheme().palette.mode === 'dark';
  const shown = segments.filter((s) => s.count > 0);
  return (
    <Box>
      <Box
        sx={{ display: 'flex', height: 28, borderRadius: 1, overflow: 'hidden', mt: 0.5 }}
        role="img"
        aria-label={segments
          .map((s) => `${s.label} ${s.count.toLocaleString()}`)
          .join(', ')}
      >
        {shown.map((s) => {
          const share = s.count / total;
          return (
            <Box
              key={s.key}
              title={`${s.label}: ${s.count.toLocaleString()}`}
              sx={{
                flexGrow: share,
                flexBasis: 0,
                bgcolor: hueColor(s.hue, dark),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: 12,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {share >= 0.08 ? s.count.toLocaleString() : ''}
            </Box>
          );
        })}
      </Box>
      {/*
        ⚠️ The legend names EVERY segment including a zero one, because "none of
        these were publishable" is a fact about the window and an absent legend
        entry reads as a missing row.
      */}
      <Stack
        direction="row"
        spacing={3}
        sx={{ mt: 1, flexWrap: 'wrap', rowGap: 1 }}
      >
        {segments.map((s) => (
          <Stack key={s.key} direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: hueColor(s.hue, dark),
                flexShrink: 0,
              }}
            />
            <Typography variant="body2" color="text.secondary">
              {s.label} {s.count.toLocaleString()} ({Math.round((s.count / total) * 100)}%)
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

/** One refusal, with the sentence the vocabulary carries for it. */
function ReasonCard({
  outcome,
  count,
  meaning,
  share,
}: {
  outcome: string;
  count: number;
  meaning: string;
  share: string | null;
}): JSX.Element {
  return (
    <Stack
      direction="row"
      spacing={2}
      alignItems="flex-start"
      sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}
    >
      <Box sx={{ flexGrow: 1 }}>
        {/*
          ⚠️ The LABEL, not the enum — "gated reliability" is engineering
          vocabulary on a screen a transport manager reads. The stored value
          stays in the tooltip: it is what the API accepts, what an audit row
          carries and what an integrator reads in the spec, so hiding it
          entirely would leave the one person who needs it with nowhere to look.
        */}
        <Typography variant="body2" sx={{ fontWeight: 600 }} title={outcome}>
          {outcomeLabel(outcome)}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          {meaning}
        </Typography>
      </Box>
      <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
        <Typography variant="h6" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {count.toLocaleString()}
        </Typography>
        {share && (
          <Typography variant="caption" color="text.secondary">
            {share} of all
          </Typography>
        )}
      </Box>
    </Stack>
  );
}
